import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Field, Area } from "./FormFields";
import { ConsentCheckboxes } from "./ConsentCheckboxes";
import {
  ContactFields,
  PointToPointFields,
  PrivateItineraryFields,
  ShoreExcursionFields,
  HoursDispositionFields,
} from "./inquiry/fields";
import { useFormErrors } from "../hooks/useFormErrors";
import { validateInquiry } from "../lib/formValidation";
import { submitInquiry } from "../lib/api";
import { fireConversion, CONVERSIONS } from "../lib/gtag";
import { PASSENGER_OPTIONS, SERVICE_TYPES } from "../data/content";
import { useT, useLang } from "../i18n/LanguageContext";
import { localizeServiceTypes, localizePassengers } from "../i18n/content_it";

const reveal = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: "auto" },
  exit: { opacity: 0, height: 0 },
  transition: { duration: 0.35 },
};

export default function InquiryForm() {
  const t = useT();
  const { lang } = useLang();
  const serviceTypes = localizeServiceTypes(SERVICE_TYPES, lang);
  const passengerOptions = localizePassengers(PASSENGER_OPTIONS, lang, t("form.paxOnRequest"));
  const modeOptions = [
    { value: "address", label: t("form.optAddress") },
    { value: "airport", label: t("form.optAirport") },
    { value: "port", label: t("form.optPort") },
    { value: "other", label: t("form.optOther") },
  ];

  const [form, setForm] = useState({
    full_name: "", email: "", confirm_email: "", passengers: "", service_date: "", pickup_time: "",
    service_type: "", notes: "",
  });
  const [d, setD] = useState({});
  const [pickupMode, setPickupMode] = useState("address");
  const [returnTrip, setReturnTrip] = useState(false);
  const [returnMode, setReturnMode] = useState("address");
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeCancellation, setAgreeCancellation] = useState(false);
  const { errors, setErrors, clearErr } = useFormErrors();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); clearErr(k); };
  const setDetail = (k) => (e) => { setD((p) => ({ ...p, [k]: e.target.value })); clearErr(k); };
  const changePickupMode = (e) => {
    setPickupMode(e.target.value);
    ["pickup_address", "city_of_pickup", "flight_number", "flight_origin", "hotel", "dropoff_hotel", "dropoff_address", "port_name"].forEach(clearErr);
  };
  // hotel & address are an "at least one" pair — clear their errors together
  const setLoc = (k) => (e) => { setD((p) => ({ ...p, [k]: e.target.value })); ["hotel", "pickup_address", "dropoff_address"].forEach(clearErr); };
  const setDropLoc = (k) => (e) => { setD((p) => ({ ...p, [k]: e.target.value })); ["dropoff_hotel", "dropoff_address"].forEach(clearErr); };
  const changeReturnMode = (e) => {
    setReturnMode(e.target.value);
    ["return_address", "return_flight_number", "return_flight_origin", "return_port_name"].forEach(clearErr);
  };

  const st = form.service_type;

  const submit = async (e) => {
    e.preventDefault();
    const errs = validateInquiry({ form, d, pickupMode, returnTrip, returnMode, agreePrivacy, agreeCancellation });
    if (Object.keys(errs).length) {
      setErrors(errs);
      if (form.email && form.confirm_email && form.email !== form.confirm_email)
        toast.error(t("form.toastEmailMismatch"));
      else
        toast.error(t("form.toastRequired"));
      return;
    }
    setErrors({});
    const details = { ...d };
    const PICKUP_LABELS = { address: "Address", airport: "Airport", port: "Port", other: "Other" };
    if (st === "point-to-point-transfer") {
      details.pickup_type = PICKUP_LABELS[pickupMode] || pickupMode;
      if (returnTrip) {
        details.return_trip = "Yes";
        details.return_pickup_type = PICKUP_LABELS[returnMode] || returnMode;
      }
    }
    setLoading(true);
    try {
      await submitInquiry({ kind: "landing", ...form, details });
      setForm({ full_name: "", email: "", confirm_email: "", passengers: "", service_date: "", pickup_time: "", service_type: "", notes: "" });
      setD({}); setReturnTrip(false); setReturnMode("address"); setPickupMode("address"); setAgreePrivacy(false); setAgreeCancellation(false);
      setSubmitted(true);
      fireConversion(CONVERSIONS.contactForm);
    } catch {
      toast.error(t("form.toastError"));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        data-testid="inquiry-success"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.33, 1, 0.68, 1] }}
        className="border border-[#C5A059]/30 bg-[#121212] p-10 md:p-14 text-center"
      >
        <CheckCircle2 size={48} className="text-[#C5A059] mx-auto" strokeWidth={1.2} />
        <h3 className="font-serif text-3xl md:text-4xl mt-6 mb-4">{t("form.successTitle")}</h3>
        <p className="text-white/70 max-w-md mx-auto leading-relaxed">
          {t("form.successText")}
        </p>
        <button
          data-testid="inquiry-reset"
          onClick={() => setSubmitted(false)}
          className="mt-8 border border-[#C5A059] text-[#C5A059] px-8 py-3 uppercase tracking-[0.2em] text-xs hover:bg-[#C5A059] hover:text-[#0A0A0A] transition-colors"
        >
          {t("form.sendAnother")}
        </button>
      </motion.div>
    );
  }

  const fieldProps = { t, errors, d, setDetail };

  return (
    <form onSubmit={submit} noValidate className="space-y-8" data-testid="inquiry-form">
      <ContactFields t={t} errors={errors} form={form} set={set} passengerOptions={passengerOptions} serviceTypes={serviceTypes} />

      <AnimatePresence mode="wait">
        {st === "point-to-point-transfer" && (
          <motion.div key="ptp" {...reveal} className="overflow-hidden">
            <PointToPointFields
              {...fieldProps}
              pickupMode={pickupMode}
              changePickupMode={changePickupMode}
              modeOptions={modeOptions}
              setLoc={setLoc}
              setDropLoc={setDropLoc}
              returnTrip={returnTrip}
              setReturnTrip={setReturnTrip}
              returnMode={returnMode}
              changeReturnMode={changeReturnMode}
            />
          </motion.div>
        )}

        {st === "private-itinerary" && (
          <motion.div key="pi" {...reveal} className="overflow-hidden">
            <PrivateItineraryFields {...fieldProps} />
          </motion.div>
        )}

        {st === "shore-excursions" && (
          <motion.div key="se" {...reveal} className="overflow-hidden">
            <ShoreExcursionFields {...fieldProps} />
          </motion.div>
        )}

        {(st === "hours-disposition" || st === "shopping-leisure") && (
          <motion.div key="hd" {...reveal} className="overflow-hidden">
            <HoursDispositionFields {...fieldProps} />
          </motion.div>
        )}
      </AnimatePresence>

      <Field label={t("form.notes")} testid="field-notes">
        <Area testid="input-notes" value={form.notes} onChange={set("notes")} placeholder={t("form.notesPh")} />
      </Field>

      <ConsentCheckboxes
        className="pt-2"
        agreePrivacy={agreePrivacy}
        setAgreePrivacy={setAgreePrivacy}
        agreeCancellation={agreeCancellation}
        setAgreeCancellation={setAgreeCancellation}
        errors={errors}
        clearErr={clearErr}
        privacyTestId="agree-privacy"
        cancellationTestId="agree-cancellation"
      />

      <button
        type="submit"
        data-testid="inquiry-submit"
        disabled={loading}
        className="bg-[#C5A059] text-[#0A0A0A] px-10 py-4 uppercase tracking-[0.2em] text-sm hover:bg-[#D4AF37] transition-colors disabled:opacity-60 w-full sm:w-auto"
      >
        {loading ? t("form.sending") : t("form.send")}
      </button>
    </form>
  );
}
